export interface BaseFilterConfig {
  q?: string 
  order?: string 
  [key: string]: any 
}

export interface SearchableFields {
  [key: string]: (item: any) => string | string[] | null | undefined
}

export const searchItems = <T>(
  items: T[],
  searchQuery: string,
  searchableFields: SearchableFields
): T[] => {
  if (!searchQuery.trim()) return items

  const query = searchQuery.toLowerCase().trim()

  return items.filter((item) => {
    return Object.values(searchableFields).some((fieldExtractor) => {
      const fieldValue = fieldExtractor(item)
      
      if (Array.isArray(fieldValue)) {
        return fieldValue.some(val => 
          val?.toLowerCase().includes(query)
        )
      }
      
      return fieldValue?.toLowerCase().includes(query)
    })
  })
}

const compareValues = (valueA: any, valueB: any, isDesc: boolean): number => {
  if (valueA === null || valueA === undefined) return isDesc ? 1 : -1
  if (valueB === null || valueB === undefined) return isDesc ? -1 : 1

  if (valueA instanceof Date && valueB instanceof Date) {
    const diff = valueA.getTime() - valueB.getTime()
    return isDesc ? -diff : diff
  }

  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return isDesc ? valueB - valueA : valueA - valueB
  }

  const strA = String(valueA)
  const strB = String(valueB)

  const dateA = new Date(strA).getTime()
  const dateB = new Date(strB).getTime()
  
  if (!isNaN(dateA) && !isNaN(dateB)) {
    return isDesc ? dateB - dateA : dateA - dateB
  }

  return isDesc ? strB.localeCompare(strA) : strA.localeCompare(strB)
}

export const sortItems = <T>(
  items: T[],
  order?: string,
  sortableFields?: { [key: string]: (item: T) => any }
): T[] => {
  if (!order || !sortableFields) return items

  const isDesc = order.startsWith("-")
  const field = isDesc ? order.slice(1) : order
  const fieldExtractor = sortableFields[field]

  if (!fieldExtractor) return items

  return [...items].sort((a, b) => {
    const valueA = fieldExtractor(a)
    const valueB = fieldExtractor(b)
    return compareValues(valueA, valueB, isDesc)
  })
}

export const processItems = <T>(
  items: T[],
  filters: BaseFilterConfig,
  searchableFields: SearchableFields,
  sortableFields?: { [key: string]: (item: T) => any }
): T[] => {
  let processedItems = items

  if (filters.q) {
    processedItems = searchItems(processedItems, filters.q, searchableFields)
  }

  if (filters.order) {
    processedItems = sortItems(processedItems, filters.order, sortableFields)
  }

  return processedItems
}

export const createFieldExtractors = {
  string: (fieldName: string) => (item: any) => item?.[fieldName],
  nested: (path: string) => (item: any) => {
    const keys = path.split('.')
    let value = item
    for (const key of keys) {
      value = value?.[key]
    }
    return value
  },
  array: (fieldName: string, subField?: string) => (item: any) => {
    const array = item?.[fieldName]
    if (!Array.isArray(array)) return []
    if (subField) {
      return array.map(obj => obj?.[subField]).filter(Boolean)
    }
    return array
  }
}
